// Pure idle-tycoon math, kept side-effect-free so it's easy to unit test
// and easy to call from React effects.
import { BUILDINGS, BUILDING_BY_ID, INCOME_MULT, TAP_BONUS_SEC } from "./buildings";

export const STORAGE_KEY = "zombie-tycoon-save-v1";

// Cap offline earnings to 4 hours so people don't game it by leaving a tab
// open forever, and to keep the welcome-back number readable.
const OFFLINE_MAX_SEC = 4 * 60 * 60;
// Offline efficiency — players still need to come back and tap things.
const OFFLINE_EFFICIENCY = 0.5;

export function newSave() {
  return {
    // Starter capital — enough to build the first Zombie Hut (cost 10) and
    // begin the income loop without forcing the player to wait.
    gold: 50,
    lifetimeGold: 0,
    levels: {},               // { buildingId: level (1+) }   (absent = not built)
    firstBuildClaimed: [],    // building ids whose +20 coin bonus has been paid
    milestonesClaimed: [],    // milestone goldAmount (number) list
    lastTickMs: Date.now(),
  };
}

export function loadSave() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return newSave();
    const data = JSON.parse(raw);
    const merged = {
      ...newSave(),
      ...data,
      levels: { ...(data.levels || {}) },
      firstBuildClaimed: [...(data.firstBuildClaimed || [])],
      milestonesClaimed: [...(data.milestonesClaimed || [])],
    };
    // One-time welcome bonus — if the player ever ended up at "gold=0 +
    // nothing built + nothing earned", hand them the starter 50 so the
    // game doesn't deadlock.
    const built = Object.keys(merged.levels || {}).length;
    if ((merged.gold || 0) <= 0 && built === 0 && (merged.lifetimeGold || 0) <= 0) {
      merged.gold = 50;
    }
    return merged;
  } catch (_e) {
    return newSave();
  }
}

export function persistSave(save) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(save)); } catch (_e) { /* quota */ }
}

export function buildingIncomePerSec(buildingId, level) {
  if (!level || level < 1) return 0;
  const b = BUILDING_BY_ID[buildingId];
  if (!b) return 0;
  return b.baseIncome * Math.pow(INCOME_MULT, level - 1);
}

export function totalIncomePerSec(save) {
  let total = 0;
  for (const b of BUILDINGS) {
    const lvl = save.levels[b.id] || 0;
    if (lvl > 0) total += buildingIncomePerSec(b.id, lvl);
  }
  return total;
}

// Cost to UPGRADE a building from its current level → next.  When the
// building isn't built yet (level 0) this returns the first-build cost.
export function upgradeCost(buildingId, currentLevel) {
  const b = BUILDING_BY_ID[buildingId];
  if (!b) return Infinity;
  const lvl = currentLevel || 0;
  return Math.floor(b.baseCost * Math.pow(b.costMult, lvl));
}

export function isUnlocked(save, buildingId) {
  const b = BUILDING_BY_ID[buildingId];
  if (!b) return false;
  if ((save.levels[buildingId] || 0) > 0) return true;          // built
  return (save.lifetimeGold || 0) >= b.unlockGold;
}

// ---- Mutators (return new save objects, never mutate input) -------------

export function applyTick(save, nowMs) {
  const lastMs = save.lastTickMs || nowMs;
  const dt = Math.max(0, (nowMs - lastMs) / 1000);
  if (dt <= 0) return save;
  const rate = totalIncomePerSec(save);
  const gain = rate * dt;
  return {
    ...save,
    gold: save.gold + gain,
    lifetimeGold: (save.lifetimeGold || 0) + gain,
    lastTickMs: nowMs,
  };
}

// Used on mount to grant offline progress.
export function applyOfflineGain(save, nowMs) {
  const lastMs = save.lastTickMs || nowMs;
  const dt = Math.max(0, Math.min(OFFLINE_MAX_SEC, (nowMs - lastMs) / 1000));
  const rate = totalIncomePerSec(save);
  const gain = rate * dt * OFFLINE_EFFICIENCY;
  return {
    next: { ...save, gold: save.gold + gain, lifetimeGold: (save.lifetimeGold || 0) + gain, lastTickMs: nowMs },
    secs: dt,
    gain,
  };
}

export function tryUpgrade(save, buildingId) {
  if (!isUnlocked(save, buildingId)) return null;
  const lvl = save.levels[buildingId] || 0;
  const cost = upgradeCost(buildingId, lvl);
  if (save.gold < cost) return null;
  return {
    ...save,
    gold: save.gold - cost,
    levels: { ...save.levels, [buildingId]: lvl + 1 },
  };
}

export function tapCollect(save, buildingId) {
  // Tapping a building grants TAP_BONUS_SEC × THAT building's own income.
  const lvl = save.levels[buildingId] || 0;
  if (lvl <= 0) return save;
  const gain = buildingIncomePerSec(buildingId, lvl) * TAP_BONUS_SEC;
  return {
    ...save,
    gold: save.gold + gain,
    lifetimeGold: (save.lifetimeGold || 0) + gain,
  };
}

// ---- Number formatting (idle games show K/M/B/T/Qa/Qi) ------------------
const SUFFIXES = ["", "K", "M", "B", "T", "Qa", "Qi", "Sx", "Sp", "Oc", "No", "Dc"];
export function fmtGold(n) {
  if (!isFinite(n)) return "∞";
  if (n < 1) return "0";
  if (n < 1000) return Math.floor(n).toString();
  const tier = Math.floor(Math.log10(n) / 3);
  if (tier < SUFFIXES.length) {
    const scaled = n / Math.pow(10, tier * 3);
    return `${scaled.toFixed(scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2)}${SUFFIXES[tier]}`;
  }
  return n.toExponential(2);
}

export function fmtRate(n) {
  return `${fmtGold(n)} /s`;
}
